import { useEffect, useState } from 'react';
import BottomNav from '../components/BottomNav';
import OnlineBadge from '../components/OnlineBadge';
import DailyLogDoc from '../components/DailyLogDoc';
import { operatorApi, type DashboardData } from '../api/operator';
import { shareDailyLogPdf } from '../lib/shareDailyLog';
import { readCache, writeCache } from '../lib/swr';

/** Operator-facing daily logbook — read-only. */
export default function DailyLogView() {
  const [equip, setEquip] = useState<Array<{ equipmentId: string; name: string }>>([]);
  const [equipmentId, setEquipmentId] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [applied, setApplied] = useState<{ equipmentId: string; date: string } | null>(null);
  const [doc, setDoc] = useState<Awaited<ReturnType<typeof operatorApi.dailyLog>> | null>(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfMsg, setPdfMsg] = useState('');

  async function sharePdf() {
    if (!doc?.template || !applied) return;
    setPdfBusy(true); setPdfMsg('');
    try {
      const how = await shareDailyLogPdf(doc, applied.equipmentId);
      if (how === 'downloaded') setPdfMsg('PDF downloaded.');
    } catch (e) {
      if ((e as Error)?.name !== 'AbortError') setPdfMsg('Could not create the PDF.');
    } finally {
      setPdfBusy(false);
    }
  }

  useEffect(() => {
    const seed = (d: DashboardData) => {
      const list = d.equipment
        .filter((e) => (e.category ?? 'daily') !== 'pms')
        .map((e) => ({ equipmentId: e.equipmentId, name: e.name }));
      setEquip(list);
      if (list[0]) {
        setEquipmentId((cur) => cur || list[0].equipmentId);
        setApplied((cur) => cur ?? { equipmentId: list[0].equipmentId, date });
      }
    };
    const cached = readCache<DashboardData>('operatorDashboard')?.data;
    if (cached) seed(cached);
    operatorApi.dashboard()
      .then((d) => { writeCache('operatorDashboard', d); seed(d); })
      .catch((e) => { if (!cached) setErr(e instanceof Error ? e.message : String(e)); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!applied) return;
    const key = `dailyLog:${applied.equipmentId}:${applied.date}`;
    const cached = readCache<Awaited<ReturnType<typeof operatorApi.dailyLog>>>(key)?.data ?? null;
    setErr('');
    if (cached) { setDoc(cached); setLoading(false); } else { setLoading(true); setDoc(null); }
    operatorApi.dailyLog(applied.equipmentId, applied.date)
      .then((d) => { setDoc(d); writeCache(key, d); })
      .catch((e) => { if (!cached) setErr(e instanceof Error ? e.message : String(e)); })
      .finally(() => setLoading(false));
  }, [applied]);

  return (
    <div className="app has-bottomnav dailyDoc">
      <div className="screen-only">
        <OnlineBadge />
        <h1>Daily logbook</h1>
        <p className="muted tiny" style={{ marginTop: -6 }}>Daily-reading equipment only. PMS checks have their own report, shared after each visit.</p>
        <div className="filters">
          <div><label>Equipment</label>
            <select value={equipmentId} onChange={(e) => setEquipmentId(e.target.value)}>
              {equip.map((e) => <option key={e.equipmentId} value={e.equipmentId}>{e.name}</option>)}
            </select>
          </div>
          <div><label>Date</label><input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
          <button className="sm" onClick={() => setApplied({ equipmentId, date })}>Show</button>
          <button className="secondary sm" onClick={() => window.print()} disabled={!doc}>🖨 Print</button>
          <button className="secondary sm" onClick={sharePdf} disabled={!doc || pdfBusy}>
            {pdfBusy ? 'Preparing…' : '📤 Share PDF'}
          </button>
        </div>
        {(err || pdfMsg) && <p className={pdfMsg === 'PDF downloaded.' ? 'muted' : 'error'}>{err || pdfMsg}</p>}
        {loading && <div className="spinner" />}
      </div>

      {doc && doc.template && <DailyLogDoc data={doc} equipmentId={applied!.equipmentId} />}

      <BottomNav />
    </div>
  );
}
